import type {
  Course,
  CreateCourseInput,
  UpdateCourseInput,
  Module,
  CreateModuleInput,
  UpdateModuleInput,
  Lesson,
  CreateLessonInput,
  UpdateLessonInput,
  Question,
  CreateQuestionInput,
  UpdateQuestionInput,
  ProgressStatus,
  Progress,
  WebinarRecording,
} from '@kennisbank/shared';
import { apiGet, apiPost, apiPatch, apiDelete } from '../../api/client.js';

/**
 * Dunne REST-laag voor de E-learning admin (Beheer → E-learning).
 *
 * Poort van de MoveLogic TanStack-Query-hooks (`useCourses`) naar de
 * kennisbank api-client. Alle paden hangen onder de suppohrt_admin-gated
 * backend-basis; de routes geven 404 als `ELEARNING_ENABLED` uit staat.
 *
 * Bewust géén eigen caching/optimistic-machinerie: net als de andere
 * beheer-pagina's herlaadt de aanroeper na een mutatie de cursusdetail.
 */

export const ELEARNING_BASE = '/api/v1/beheer/elearning';

/** Cursusdetail zoals GET /courses/:courseId hem teruggeeft. */
export interface ModuleWithChildren extends Module {
  lessons: Lesson[];
  questions: Question[];
}
export interface CourseDetail {
  course: Course;
  modules: ModuleWithChildren[];
}

/** Eén rij in het voortgangsdashboard (GET /courses/:courseId/dashboard). */
export interface DashboardRow {
  userId: string;
  status: ProgressStatus;
  progressPercentage: number;
  completedLessons: number;
  totalLessons: number;
  videoQuestionsAnswered: number;
  videoQuestionsTotal: number;
  examAttemptsCount: number;
  bestExamScore: number | null;
  startedAt: string;
  completedAt: string | null;
  updatedAt: string;
  /** Uit het gebruikersprofiel; null als de gebruiker niet (meer) bestaat. */
  name: string | null;
  email: string | null;
}

// ─── Courses ─────────────────────────────────────────────────────────────────

export function listCourses(): Promise<{ courses: Course[] }> {
  return apiGet(`${ELEARNING_BASE}/courses`);
}

export function getCourse(courseId: string): Promise<CourseDetail> {
  return apiGet(`${ELEARNING_BASE}/courses/${courseId}`);
}

export function createCourse(body: CreateCourseInput): Promise<Course> {
  return apiPost(`${ELEARNING_BASE}/courses`, body);
}

export function updateCourse(courseId: string, body: UpdateCourseInput): Promise<Course> {
  return apiPatch(`${ELEARNING_BASE}/courses/${courseId}`, body);
}

export function deleteCourse(courseId: string): Promise<void> {
  return apiDelete(`${ELEARNING_BASE}/courses/${courseId}`);
}

// ─── Modules ─────────────────────────────────────────────────────────────────

export function createModule(courseId: string, body: CreateModuleInput): Promise<Module> {
  return apiPost(`${ELEARNING_BASE}/courses/${courseId}/modules`, body);
}

export function updateModule(
  courseId: string,
  moduleId: string,
  body: UpdateModuleInput,
): Promise<Module> {
  return apiPatch(`${ELEARNING_BASE}/courses/${courseId}/modules/${moduleId}`, body);
}

export function deleteModule(courseId: string, moduleId: string): Promise<void> {
  return apiDelete(`${ELEARNING_BASE}/courses/${courseId}/modules/${moduleId}`);
}

export function reorderModules(courseId: string, ids: string[]): Promise<{ ok: true }> {
  return apiPost(`${ELEARNING_BASE}/courses/${courseId}/modules/reorder`, { ids });
}

// ─── Lessons (nested onder cursus + moduul) ──────────────────────────────────

export function createLesson(
  courseId: string,
  moduleId: string,
  body: CreateLessonInput,
): Promise<Lesson> {
  return apiPost(`${ELEARNING_BASE}/courses/${courseId}/modules/${moduleId}/lessons`, body);
}

export function updateLesson(
  courseId: string,
  moduleId: string,
  lessonId: string,
  body: UpdateLessonInput,
): Promise<Lesson> {
  return apiPatch(
    `${ELEARNING_BASE}/courses/${courseId}/modules/${moduleId}/lessons/${lessonId}`,
    body,
  );
}

export function deleteLesson(courseId: string, moduleId: string, lessonId: string): Promise<void> {
  return apiDelete(`${ELEARNING_BASE}/courses/${courseId}/modules/${moduleId}/lessons/${lessonId}`);
}

export function reorderLessons(
  courseId: string,
  moduleId: string,
  ids: string[],
): Promise<{ ok: true }> {
  return apiPost(`${ELEARNING_BASE}/courses/${courseId}/modules/${moduleId}/lessons/reorder`, {
    ids,
  });
}

/** Signed upload-URL voor een lesvideo; het videoPath gaat mee in de les-save. */
export function mintLessonVideoUploadUrl(
  courseId: string,
  moduleId: string,
  body: { mimeType: string; sizeBytes: number },
): Promise<{ uploadUrl: string; videoPath: string; expiresAt: string }> {
  return apiPost(
    `${ELEARNING_BASE}/courses/${courseId}/modules/${moduleId}/video-upload-url`,
    body,
  );
}

/** PUT naar de signed URL met voortgangs-callback (fetch kent geen upload-progress). */
export function putWithProgress(
  url: string,
  file: File,
  onProgress: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('content-type', file.type);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`upload failed: ${xhr.status}`));
    xhr.onerror = () => reject(new Error('upload failed'));
    xhr.send(file);
  });
}

// ─── Webinar-opnames ─────────────────────────────────────────────────────────

/** Opname zoals de lijst hem levert: het objectpad blijft op de backend. */
export type WebinarListItem = Omit<WebinarRecording, 'videoPath'>;

// Lezen gaat via de cursistenroute; beheer heeft geen eigen lijst-endpoint.
export function listWebinars(): Promise<{ webinars: WebinarListItem[] }> {
  return apiGet('/api/v1/elearning/webinars');
}

export function mintWebinarUploadUrl(body: {
  mimeType: string;
  sizeBytes: number;
}): Promise<{ uploadUrl: string; videoPath: string; expiresAt: string }> {
  return apiPost(`${ELEARNING_BASE}/webinars/upload-url`, body);
}

export function createWebinar(body: {
  title: string;
  date: string | null;
  videoPath: string;
}): Promise<WebinarRecording> {
  return apiPost(`${ELEARNING_BASE}/webinars`, body);
}

export function deleteWebinar(id: string): Promise<void> {
  return apiDelete(`${ELEARNING_BASE}/webinars/${id}`);
}

// ─── Questions (nested onder cursus + moduul) ────────────────────────────────

export function createQuestion(
  courseId: string,
  moduleId: string,
  body: CreateQuestionInput,
): Promise<Question> {
  return apiPost(`${ELEARNING_BASE}/courses/${courseId}/modules/${moduleId}/questions`, body);
}

export function updateQuestion(
  courseId: string,
  moduleId: string,
  questionId: string,
  body: UpdateQuestionInput,
): Promise<Question> {
  return apiPatch(
    `${ELEARNING_BASE}/courses/${courseId}/modules/${moduleId}/questions/${questionId}`,
    body,
  );
}

export function deleteQuestion(
  courseId: string,
  moduleId: string,
  questionId: string,
): Promise<void> {
  return apiDelete(
    `${ELEARNING_BASE}/courses/${courseId}/modules/${moduleId}/questions/${questionId}`,
  );
}

export function reorderQuestions(
  courseId: string,
  moduleId: string,
  ids: string[],
): Promise<{ ok: true }> {
  return apiPost(`${ELEARNING_BASE}/courses/${courseId}/modules/${moduleId}/questions/reorder`, {
    ids,
  });
}

// ─── Dashboard ───────────────────────────────────────────────────────────────

export function getDashboard(
  courseId: string,
): Promise<{ rows: DashboardRow[]; hasExam: boolean }> {
  return apiGet(`${ELEARNING_BASE}/courses/${courseId}/dashboard`);
}

export function resetRetakes(courseId: string, userId: string): Promise<{ progress: Progress }> {
  return apiPost(`${ELEARNING_BASE}/courses/${courseId}/progress/${userId}/reset-retakes`, {});
}
